import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { MESSAGE_REFRESH_INTERVAL } from '../../../pages/MessageList';
import { dispatch } from '../../../redux/store';
import { notifyError } from '../../../utils/helpers';
import BottomNavigation from '../../reusable/BottomNav';
import ChatHeader from '../../reusable/chatitems/ChatHeader';
import ChatInput from '../../reusable/chatitems/ChatInput';
import ChatMessage from '../../reusable/chatitems/ChatMessage';
import Spinner from '../../reusable/spinner/Spinner';

const DirectMessage = () => {
    const { pathname } = useLocation();
    const [searchParams] = useSearchParams();
    const recipientId = useMemo(
        () => searchParams.get('recipientId'),
        [searchParams]
    );
    const temporaryChatroomId = useMemo(() => uuidv4(), []);

    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [chatroomId, setChatroomId] = useState(
        searchParams.get('chatroomId')
    );
    const [prevMessagesLength, setPrevMessagesLength] = useState(0);
    const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);

    const userInfo = useSelector(state => state.user.data);
    const chatroom = useSelector(state =>
        state.chat.chats?.find(
            chat =>
                (chatroomId && chat.chatroomId === chatroomId) ||
                chat.temporaryChatroomId === temporaryChatroomId
        )
    );
    const otherUser = chatroom?.otherUser;
    const messages = chatroom?.messages || [];
    const pagination = chatroom?.pagination;

    const fetchChat = async (cursor = null) => {
        try {
            if (cursor) setIsLoadingMore(true);
            await dispatch.chat.getChat({ chatroomId, cursor });
        } catch (error) {
            notifyError(error?.message || 'Failed to fetch chat.');
        } finally {
            if (cursor) setIsLoadingMore(false);
            if (isFirstLoad) setIsFirstLoad(false);
        }
    };

    const fetchChatByRecipient = async () => {
        try {
            const data = await dispatch.chat.getChatByRecipient({
                recipientId,
                temporaryChatroomId
            });
            if (data?.chatroomId) {
                setChatroomId(data.chatroomId);
            }
        } finally {
            setIsFirstLoad(false);
        }
    };

    const fetchOtherUser = async () => {
        await dispatch.chat.getOtherUser({
            chatroomId,
            temporaryChatroomId,
            recipientId
        });
    };

    useEffect(() => {
        const fetchData = async () => {
            await fetchOtherUser();
            if (chatroomId) {
                await fetchChat();
            } else if (recipientId) {
                await fetchChatByRecipient();
            }
        };

        fetchData();

        return async () => {
            await dispatch.chat.clearTemporaryChats();
        };
    }, []);

    useEffect(() => {
        if (chatroomId) {
            searchParams.set('chatroomId', chatroomId);
        } else {
            searchParams.delete('chatroomId');
        }

        window.history.replaceState(
            null,
            '',
            `${pathname}?${searchParams.toString()}`
        );
    }, [chatroomId]);

    const sendMessage = async data => {
        try {
            let chatId = chatroomId;
            const temporaryMessageId = uuidv4();

            dispatch.chat.createTemporaryMessage({
                chatroomId: chatId,
                temporaryChatroomId,
                temporaryMessageId,
                userInfo,
                data
            });

            if (!chatId) {
                chatId = await dispatch.chat.createChat({
                    recipientId,
                    temporaryChatroomId
                });
                setChatroomId(chatId);
            }

            await dispatch.chat.createMessage({
                chatroomId: chatId,
                temporaryChatroomId,
                temporaryMessageId,
                data
            });
        } catch (error) {
            console.log(error);
            notifyError('Error sending message');
        }
    };

    const sendTextMessage = message =>
        sendMessage({
            type: 'text',
            message
        });

    const sendImageMessage = photoUrl =>
        sendMessage({
            type: 'image',
            image_url: photoUrl
        });

    useEffect(() => {
        if (!chatroomId) return;

        const intervalId = setInterval(() => {
            fetchChat();
        }, MESSAGE_REFRESH_INTERVAL);

        return () => {
            clearInterval(intervalId);
        };
    }, [chatroomId]);

    useEffect(() => {
        if (messages.length > prevMessagesLength) {
            setShouldScrollToBottom(messages.length - prevMessagesLength === 1);
        } else {
            setShouldScrollToBottom(true);
        }
        setPrevMessagesLength(messages.length);
    }, [messages.length]);

    return (
        <div className='pb-[70px] page'>
            <ChatHeader userDetails={otherUser} />
            {isFirstLoad && !messages.length ? (
                <div className='flex flex-col items-center justify-center flex-1 w-full h-full pt-[79px] mb-[79px]'>
                    <Spinner />
                </div>
            ) : (
                <>
                    {pagination?.hasMore && (
                        <div className='flex justify-center mb-2 pt-[100px]'>
                            {isLoadingMore ? (
                                <Spinner />
                            ) : (
                                <button
                                    disabled={isLoadingMore}
                                    className='px-4 py-2 text-xs bg-gray-200 rounded hover:bg-gray-300'
                                    onClick={() =>
                                        fetchChat(pagination.nextCursor)
                                    }
                                >
                                    Load More
                                </button>
                            )}
                        </div>
                    )}
                    <ChatMessage
                        chats={messages}
                        shouldScrollToBottom={shouldScrollToBottom}
                    />
                </>
            )}
            <ChatInput
                sendTextMessage={sendTextMessage}
                sendImageMessage={sendImageMessage}
            />
            <BottomNavigation />
        </div>
    );
};

export default React.memo(DirectMessage);
